/**
 * /tracking/qr — QR code tracking page (feature #170).
 *
 * Declares a TanStack Router route and renders the `QrTrackingPage` feature.
 * The component is picked up by the root-owned `router.tsx` module
 * augmentation during the wire-up cycle — agents never touch `router.tsx`.
 *
 * `?affiliateId=` is reserved so operators can deep-link into a specific
 * publisher's QR code roster from an affiliate detail view.
 */

import { createRoute, Link } from "@tanstack/react-router";
import { rootRoute } from "./__root";
import { QrTrackingPage } from "@/features/qr-tracking/QrTrackingPage";
import { isValidAffiliateId } from "@/features/qr-tracking/api";

interface TrackingQrSearch {
	affiliateId?: string;
}

export const trackingQrRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/tracking/qr",
	validateSearch: (raw: Record<string, unknown>): TrackingQrSearch => {
		const v = raw.affiliateId;
		if (typeof v === "string" && isValidAffiliateId(v)) {
			return { affiliateId: v };
		}
		return {};
	},
	component: TrackingQrRoute,
});

export default trackingQrRoute;

// ---------------------------------------------------------------------------
// Page shell — breadcrumb + the live feature component.
// ---------------------------------------------------------------------------

export function TrackingQrRoute() {
	const { affiliateId } = trackingQrRoute.useSearch();

	return (
		<div className="mx-auto flex w-full max-w-5xl flex-col gap-4 px-6 pt-6 sm:pt-10">
			<Breadcrumb />
			<QrTrackingPage initialAffiliateId={affiliateId} />
		</div>
	);
}

function Breadcrumb() {
	return (
		<nav aria-label="Breadcrumb" className="text-sm text-zinc-500">
			<ol className="flex flex-wrap items-center gap-1">
				<li>
					<Link
						to="/"
						className="rounded-[6px] px-1 py-0.5 hover:bg-zinc-100 hover:text-zinc-900"
					>
						Dashboard
					</Link>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li>
					<span className="rounded-[6px] px-1 py-0.5">Tracking</span>
				</li>
				<li aria-hidden="true" className="text-zinc-300">
					/
				</li>
				<li className="text-zinc-900">QR codes</li>
			</ol>
		</nav>
	);
}

// Named re-export for the root wire-up cycle.
export { TrackingQrRoute as TrackingQrPage };
